import { useEffect, useRef, useState } from 'react'
import { Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { addIntervention } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'

const NOTE_MAX_LENGTH = 1000

// The BE forwards domain guard messages in English (INVALID_OPERATION/INVALID_ARGUMENT) —
// map the reachable codes to French; other envelopes (PROJECT_NOT_FOUND, PHASE_CONFLICT)
// already carry a French message.
function submitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'INVALID_OPERATION') {
      return "La pose initiale doit être en cours ou terminée avant de planifier une intervention."
    }
    if (err.code === 'INVALID_ARGUMENT') {
      return 'Données invalides — vérifiez la note saisie.'
    }
    if (err.message) return err.message
  }
  return "Impossible de créer l'intervention"
}

interface AddInterventionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  /** Fired after a successful POST — the drawer chains its load() + onMutated() refetch. */
  onCreated: () => void
}

/**
 * « Planifier une intervention » (SOLY-FINITION-001 MS-03): creates a second pose (+ optional
 * re-métré) after the initial one. Deliberately minimal — there is no nature to choose or to
 * preview: since SOLY-PLAN-003 every second intervention is a finition. The dialog used to fetch
 * the project's invoices on open just to announce Finition-or-SAV; that lookup is gone with the
 * distinction it served.
 */
export function AddInterventionDialog({ open, onOpenChange, projectId, onCreated }: AddInterventionDialogProps) {
  const [withSurvey, setWithSurvey] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A rejection landing after the dialog was closed must not repopulate the error state
  // (the close-reset already ran) — the next open would show a stale banner.
  const openRef = useRef(open)
  useEffect(() => {
    openRef.current = open
  }, [open])

  // Reset state on close so the next open starts clean.
  useEffect(() => {
    if (!open) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setWithSurvey(false)
      setNote('')
      setSubmitting(false)
      setError(null)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open])

  const handleSubmit = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const trimmed = note.trim()
      await addIntervention(projectId, { withSurvey, note: trimmed.length > 0 ? trimmed : null })
      // No success toast: the new pose appearing in the drawer IS the feedback.
      onCreated()
      onOpenChange(false)
    } catch (err) {
      if (!openRef.current) return
      setError(submitErrorMessage(err))
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="planning-add-intervention-dialog">
        <DialogHeader>
          <DialogTitle>Planifier une intervention</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="planning-add-intervention-survey"
              data-testid="planning-add-intervention-survey"
              checked={withSurvey}
              onCheckedChange={(v) => setWithSurvey(v === true)}
              disabled={submitting}
            />
            <Label htmlFor="planning-add-intervention-survey" className="text-sm font-normal">
              Un nouveau métré est nécessaire
            </Label>
          </div>

          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
            placeholder="Raison / contexte (optionnel)"
            rows={3}
            maxLength={NOTE_MAX_LENGTH}
            disabled={submitting}
            data-testid="planning-add-intervention-note"
          />

          {/* States what is being created — the badge on the new pose will read the same word.
              A statement, not a prediction: there is nothing left to derive. */}
          <p
            data-testid="planning-add-intervention-nature"
            className="flex items-start gap-1.5 text-xs text-muted-foreground"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
            <span><span className="font-medium text-foreground">Finition</span> — un retour sur le chantier après la pose initiale</span>
          </p>

          {error && (
            <div
              data-testid="planning-add-intervention-error"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="planning-add-intervention-cancel"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="planning-add-intervention-submit"
          >
            {submitting ? 'Création…' : "Créer l'intervention"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
